import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ScrollText, Download, Search, Pencil, Trash2, RotateCcw, ChevronDown, ChevronUp, History, ShoppingCart, Camera, Loader2, Package, Truck, CheckCircle2, BarChart2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { posAPI, purchasesAPI } from '@/lib/api';
import ReceiptModal from '@/components/ReceiptModal';
import CameraScanner from '@/components/CameraScanner';
import { useAuth } from '@/contexts/AuthContext';
import { useOffline } from '@/contexts/OfflineContext';
import { getSalesByDateRange, getAllQueueItems, putSalesCache, getAllProducts } from '@/lib/offlineDB';
import { useIsMobile } from '@/hooks/useIsMobile';
import SalesOverviewChart, { PERIODS, CUSTOM_PERIOD_INDEX } from '@/components/dashboard/SalesOverviewChart';

// ── Date helpers ──────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase();
};
const fmt = (n) => '₹' + Number(n ?? 0).toFixed(2);

// ── Mode badge ────────────────────────────────────────────────────────────────

const MODE_COLORS = {
  cash:  'bg-emerald-100 text-emerald-700',
  card:  'bg-blue-100 text-blue-700',
  upi:   'bg-purple-100 text-purple-700',
  khata: 'bg-orange-100 text-orange-700',
  split: 'bg-gray-100 text-gray-700',
};

function ModeBadge({ mode }) {
  const cls = MODE_COLORS[mode] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>
      {(mode || '').charAt(0).toUpperCase() + (mode || '').slice(1)}
    </span>
  );
}

function DeliveryStatusPill({ status }) {
  if (!status) return null;
  if (status === 'in_transit') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 font-medium whitespace-nowrap">
        <Truck className="w-3 h-3 shrink-0" />
        In Transit
      </span>
    );
  }
  if (status === 'delivered') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-medium whitespace-nowrap">
        <CheckCircle2 className="w-3 h-3 shrink-0" />
        Delivered
      </span>
    );
  }
  if (status === 'cancelled') {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 whitespace-nowrap">Cancelled</span>;
  }
  if (status === 'rejected') {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 whitespace-nowrap">Rejected</span>;
  }
  return null;
}

// ── CSV export ────────────────────────────────────────────────────────────────

function exportCsv(rows) {
  const headers = ['Date', 'Time', 'Bill No', 'Customer', 'Payment Mode', 'Items', 'GST', 'Total', 'Status'];
  const lines = rows.map(r => [
    fmtDate(r.created_at),
    fmtTime(r.created_at),
    r.bill_number,
    r.customer_name || 'Walk-in',
    r.payment_mode,
    (Array.isArray(r.items) ? r.items : []).map(i => `${i.product_name} x${i.quantity}`).join('; '),
    Number(r.total_gst ?? 0).toFixed(2),
    Number(r.total_amount ?? 0).toFixed(2),
    r.status,
  ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
  const csv = [headers.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `sales-history-${today()}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function exportProductCsv(rows, productName) {
  const headers = ['Date', 'Time', 'Bill No', 'Product', 'Qty', 'Unit Price', 'Discount', 'GST Amount', 'Item Total', 'Payment Mode'];
  const lines = rows.map(r => [
    fmtDate(r._sale_date),
    fmtTime(r._sale_date),
    r._bill_number,
    r.product_name,
    r.quantity,
    Number(r.unit_price ?? 0).toFixed(2),
    r.discount > 0 ? (r.discount_type === 'percent' ? `${r.discount}%` : Number(r.discount).toFixed(2)) : '0',
    Number(r.gst_amount ?? 0).toFixed(2),
    Number(r.item_total ?? 0).toFixed(2),
    r._payment_mode,
  ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
  const csv = [headers.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = `product-sales-${(productName || 'product').replace(/\s+/g, '-')}-${today()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Offline helpers ───────────────────────────────────────────────────────────

const calcItemGst = (item) => {
  const total = item.qty * item.unit_price;
  const gst = item.gst_rate > 0
    ? Math.round(total * item.gst_rate / (100 + item.gst_rate) * 100) / 100
    : 0;
  return { total: Math.round(total * 100) / 100, gst };
};

function queueItemToSaleShape(queueItem) {
  const { temp_bill_number, real_bill_number, payload, created_at, status, error, local_id } = queueItem;
  const bill_number = real_bill_number || temp_bill_number;
  const items = (payload.items || []).map(i => {
    const { total, gst } = calcItemGst(i);
    return {
      catalog_id:   i.catalog_id,
      product_name: i.product_name,
      quantity:     i.qty,
      unit_price:   i.unit_price,
      gst_rate:     i.gst_rate ?? 0,
      gst_amount:   gst,
      item_total:   total,
    };
  });
  const total_amount = Math.round(items.reduce((s, i) => s + i.item_total, 0) * 100) / 100;
  const total_gst    = Math.round(items.reduce((s, i) => s + i.gst_amount, 0) * 100) / 100;
  return {
    id:              `queue-${local_id}`,
    bill_number,
    created_at,
    items,
    total_amount,
    total_gst,
    payment_mode:    payload.payment_mode,
    split_payments:  payload.split_payments,
    customer_name:   payload.customer_name,
    customer_mobile: payload.customer_mobile,
    status:          'active',
    _offline:        true,
    _queue_status:   status,
    _error:          error,
  };
}

function mergeOfflineSales(queueItems, cachedSales) {
  const syncedRealBills = new Set(
    queueItems.filter(q => q.status === 'synced' && q.real_bill_number).map(q => q.real_bill_number)
  );
  const queueSales = queueItems
    .filter(q => q.status !== 'synced')
    .map(queueItemToSaleShape);
  const dedupedCache = cachedSales.filter(s => !syncedRealBills.has(s.bill_number) || true);
  return [...queueSales, ...dedupedCache].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

export default function SalesHistory() {
  const navigate = useNavigate();
  const { storeProfile, user } = useAuth();
  const { isOnline, isSyncing } = useOffline();
  const isMobile = useIsMobile();
  const qc = useQueryClient();

  // Filters — one page-wide period picker drives both the chart and the list
  const [activePeriod, setActivePeriod]           = useState(2); // This Month
  const [customStart, setCustomStart]             = useState(monthStart());
  const [customEnd, setCustomEnd]                 = useState(today());
  const [searchQ, setSearchQ]                     = useState('');
  const [paymentModeFilter, setPaymentModeFilter] = useState('');
  const [resolvedCatalogId, setResolvedCatalogId] = useState(null);
  const [productViewMode, setProductViewMode]     = useState(false);

  const dateRange = useMemo(() => {
    const p = PERIODS[activePeriod];
    if (p.start && p.end) return { start: p.start(), end: p.end() };
    return { start: customStart, end: customEnd };
  }, [activePeriod, customStart, customEnd]);

  const [chartTotals, setChartTotals] = useState({ revenue: 0, transactions: 0 });

  // Product catalog + dropdown
  const [allProducts, setAllProducts]     = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown]   = useState(false);
  const [selectedIdx, setSelectedIdx]     = useState(-1);

  // Data
  const [sales, setSales]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(1);

  // Scanner
  const [inlineCameraOpen, setInlineCameraOpen] = useState(false);
  const [barcodeLoading, setBarcodeLoading]   = useState(false);

  // Receipt / delete / history
  const [receiptSale, setReceiptSale]     = useState(null);
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [historyOpen, setHistoryOpen]     = useState(false);

  // Refs
  const searchRef        = useRef(null);
  const dropdownRef      = useRef(null);
  const searchQueryRef   = useRef('');    // mirrors searchQ for post-load re-run
  const catalogLoadedRef = useRef(false); // prevents duplicate fetches

  // ── Fetch sales ───────────────────────────────────────────────────────────

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      if (!navigator.onLine) throw new Error('offline');
      const r = await posAPI.listSales({
        start_date: dateRange.start, end_date: dateRange.end,
        include_deleted: true, limit: 500,
      });
      const serverSales = r.data?.sales ?? [];
      setSales(serverSales);
      putSalesCache(serverSales).catch(() => {});
      setPage(1);
    } catch {
      const [cached, queueItems] = await Promise.all([
        getSalesByDateRange(dateRange.start, dateRange.end),
        getAllQueueItems(),
      ]);
      const merged = mergeOfflineSales(queueItems, cached);
      setSales(merged);
      setPage(1);
      if (merged.length === 0 && !navigator.onLine)
        toast.info('No cached sales for this period. Connect to refresh.');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchSales(); }, [fetchSales]);

  // Skip the first run — the mount effect above already covers initial load.
  // This effect exists to re-fetch when the offline queue finishes syncing
  // (isSyncing flips true→false) or connectivity is restored after mount.
  const syncEffectRanRef = useRef(false);
  useEffect(() => {
    if (!syncEffectRanRef.current) { syncEffectRanRef.current = true; return; }
    if (isOnline && !isSyncing) fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, isSyncing]);

  // ── Product catalog — lazy load on first search focus ─────────────────────
  // Prefer the catalog already synced to IndexedDB (by POS/OfflineContext)
  // over an independent network round trip; only hit the API if that cache
  // is genuinely empty (e.g. user never opened POS on this device).

  const loadCatalog = useCallback(async () => {
    if (catalogLoadedRef.current) return;
    catalogLoadedRef.current = true;
    try {
      const cached = await getAllProducts();
      if (cached.length > 0) {
        setAllProducts(cached);
        return;
      }
      const r = await posAPI.getProducts();
      setAllProducts(r.data?.items ?? []);
    } catch {
      catalogLoadedRef.current = false; // allow retry on next focus
    }
  }, []);

  // ── Product search — same scoring priority as POS ─────────────────────────
  //
  // 0  exact barcode / SKU
  // 1  barcode / SKU prefix
  // 2  name prefix
  // 3  barcode / SKU contains
  // 4  name contains
  // 5  category / brand contains

  const doSearch = useCallback((q) => {
    if (!q.trim()) { setSearchResults([]); setShowDropdown(false); return; }
    const lower = q.toLowerCase();

    const score = (p) => {
      const bc   = String(p.barcode || '').toLowerCase();
      const sk   = String(p.sku || '').toLowerCase();
      const name = (p.name || p.product_name || '').toLowerCase();
      if (bc === lower || sk === lower)                return 0;
      if (bc.startsWith(lower) || sk.startsWith(lower)) return 1;
      if (name.startsWith(lower))                      return 2;
      if (bc.includes(lower) || sk.includes(lower))    return 3;
      if (name.includes(lower))                        return 4;
      return 5;
    };

    const results = allProducts
      .filter(p => {
        const name = (p.name || p.product_name || '').toLowerCase();
        return (
          name.includes(lower) ||
          String(p.barcode || '').toLowerCase().includes(lower) ||
          String(p.sku || '').toLowerCase().includes(lower) ||
          (p.category || '').toLowerCase().includes(lower) ||
          (p.brand || '').toLowerCase().includes(lower)
        );
      })
      .sort((a, b) => score(a) - score(b))
      .slice(0, 10);

    setSearchResults(results);
    setSelectedIdx(results.length > 0 ? 0 : -1);
    setShowDropdown(true);
  }, [allProducts]);

  // Re-run after catalog loads (fixes first-focus race condition)
  useEffect(() => {
    if (allProducts.length > 0 && searchQueryRef.current.trim())
      doSearch(searchQueryRef.current);
  }, [allProducts, doSearch]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!dropdownRef.current) return;
    dropdownRef.current.querySelectorAll('button')[selectedIdx]?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  // ── Select product from dropdown → product view ───────────────────────────

  const selectProduct = useCallback((product) => {
    const name = product.name || product.product_name || '';
    setResolvedCatalogId(product.catalog_id);
    setSearchQ(name);
    searchQueryRef.current = name;
    setProductViewMode(true);
    setShowDropdown(false);
    setSearchResults([]);
    setPage(1);
  }, []);

  // ── Camera barcode → API lookup → product view ────────────────────────────

  const handleCameraDetected = useCallback(async (barcode) => {
    setScannerOpen(false);
    if (!barcode?.trim()) return;
    setBarcodeLoading(true);
    try {
      const r = await purchasesAPI.lookupBarcode(barcode.trim());
      selectProduct(r.data);
    } catch {
      toast.error('No product found for this barcode');
    } finally {
      setBarcodeLoading(false);
    }
  }, [selectProduct]);

  // ── Search input handlers ─────────────────────────────────────────────────

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchQ(val);
    searchQueryRef.current = val;
    if (!val) {
      setResolvedCatalogId(null);
      setProductViewMode(false);
      setSearchResults([]);
      setShowDropdown(false);
    } else {
      doSearch(val);
    }
    setPage(1);
  }, [doSearch]);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showDropdown && searchResults.length > 0) { setShowDropdown(true); setSelectedIdx(0); }
      else setSelectedIdx(i => Math.min(i + 1, searchResults.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && selectedIdx >= 0 && selectedIdx < searchResults.length) {
        // Explicit product pick → product view
        selectProduct(searchResults[selectedIdx]);
      } else {
        // No product selected → bill text search (bill#, customer, item names)
        setProductViewMode(false);
        setResolvedCatalogId(null);
        setShowDropdown(false);
      }
    }
  }, [showDropdown, selectedIdx, searchResults, selectProduct]);

  const clearProductSearch = useCallback(() => {
    setResolvedCatalogId(null);
    setSearchQ('');
    searchQueryRef.current = '';
    setProductViewMode(false);
    setSearchResults([]);
    setShowDropdown(false);
    setPage(1);
  }, []);

  // ── Client-side filter ────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let result = sales;
    if (resolvedCatalogId) {
      result = result.filter(s =>
        (Array.isArray(s.items) ? s.items : []).some(i => i.catalog_id === resolvedCatalogId)
      );
    } else if (searchQ.trim()) {
      const lower = searchQ.toLowerCase();
      result = result.filter(s =>
        (s.bill_number || '').toLowerCase().includes(lower) ||
        (s.customer_name || '').toLowerCase().includes(lower) ||
        (Array.isArray(s.items) ? s.items : []).some(i =>
          (i.product_name || '').toLowerCase().includes(lower)
        )
      );
    }
    if (paymentModeFilter)
      result = result.filter(s => s.payment_mode === paymentModeFilter);
    return result;
  }, [sales, searchQ, resolvedCatalogId, paymentModeFilter]);

  // ── Product view rows ─────────────────────────────────────────────────────

  const productRows = useMemo(() => {
    if (!productViewMode) return [];
    const lower = searchQ.toLowerCase();
    const rows = [];
    for (const sale of filtered) {
      if (sale.status === 'deleted') continue;
      const items = Array.isArray(sale.items) ? sale.items : [];
      const matching = resolvedCatalogId
        ? items.filter(i => i.catalog_id === resolvedCatalogId)
        : items.filter(i => (i.product_name || '').toLowerCase().includes(lower));
      for (const item of matching) {
        rows.push({ ...item, _sale_date: sale.created_at, _bill_number: sale.bill_number, _payment_mode: sale.payment_mode });
      }
    }
    return rows;
  }, [filtered, productViewMode, resolvedCatalogId, searchQ]);

  const productSummary = useMemo(() => {
    if (!productViewMode || productRows.length === 0) return null;
    const totalQty     = productRows.reduce((s, r) => s + Number(r.quantity || 0), 0);
    const totalRevenue = productRows.reduce((s, r) => s + Number(r.item_total || 0), 0);
    const totalGst     = productRows.reduce((s, r) => s + Number(r.gst_amount || 0), 0);
    const billCount    = new Set(productRows.map(r => r._bill_number)).size;
    const avgUnitPrice = totalQty > 0 ? totalRevenue / totalQty : 0;
    return { totalQty, totalRevenue, totalGst, billCount, avgUnitPrice };
  }, [productViewMode, productRows]);

  // ── Pagination ────────────────────────────────────────────────────────────

  const mainBills    = useMemo(() => filtered.filter(s => s.status !== 'amended'), [filtered]);
  const historyBills = useMemo(() => filtered.filter(s => s.status === 'amended'),  [filtered]);
  const totalPages   = Math.max(1, Math.ceil(mainBills.length / PAGE_SIZE));
  const currentPage  = Math.min(page, totalPages);
  const pageRows     = mainBills.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const prodTotalPages  = Math.max(1, Math.ceil(productRows.length / PAGE_SIZE));
  const prodCurrentPage = Math.min(page, prodTotalPages);
  const prodPageRows    = productRows.slice((prodCurrentPage - 1) * PAGE_SIZE, prodCurrentPage * PAGE_SIZE);

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (!isOnline) { toast.warning('Cannot delete sales while offline.'); setDeleteTarget(null); return; }
    setDeleteLoading(true);
    try {
      await posAPI.deleteSale(deleteTarget);
      toast.success('Sale deleted'); setDeleteTarget(null); fetchSales();
      qc.invalidateQueries({ queryKey: ['supplier-invoices'] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally { setDeleteLoading(false); }
  };

  const handleRestore = async (billNumber) => {
    try { await posAPI.restoreSale(billNumber); toast.success('Sale restored'); fetchSales(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Restore failed'); }
  };

  const handleEdit = (sale) => {
    if (!isOnline) { toast.warning('Amendments require an internet connection.'); return; }
    if (user?.role === 'supplier' && sale.delivery_status !== 'in_transit') {
      toast.error('Cannot edit — only in-transit bills can be edited.');
      return;
    }
    navigate(user?.role === 'supplier' ? '/supplier/billing' : '/pos', { state: { amendSale: sale } });
  };

  const handleReceiptView = async (billNumber, saleFallback) => {
    if (saleFallback?._offline) { setReceiptSale(saleFallback); return; }
    try { const r = await posAPI.getSale(billNumber); setReceiptSale(r.data); }
    catch { toast.error('Could not load receipt'); }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-background">

      {/* Fixed header — compact on mobile, full on desktop */}
      {isMobile ? (
        <div className="flex-none flex items-center justify-between px-4 py-2 border-b border-border bg-card gap-3">
          <h1 className="text-base font-semibold text-foreground whitespace-nowrap shrink-0">Sales History</h1>
          <div className="inline-flex items-center gap-0.5 bg-muted rounded-lg p-0.5 shrink-0">
            <button
              onClick={() => navigate(user?.role === 'supplier' ? '/supplier/billing' : '/pos')}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
            >
              <ShoppingCart className="w-3 h-3 shrink-0" />
              POS
            </button>
            <button className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm whitespace-nowrap">
              <History className="w-3 h-3 shrink-0" />
              Sales History
            </button>
            {user?.role === 'supplier' && (
              <button
                onClick={() => navigate('/supplier/billing?view=invoices')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <Truck className="w-3 h-3 shrink-0" />
                Invoices
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-none flex items-center justify-between px-6 py-3 border-b border-border bg-card">
          <h1 className="text-base font-semibold text-foreground flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-primary" />
            Sales History
          </h1>
          <div className="inline-flex items-center gap-1 bg-muted rounded-lg p-1">
            <button onClick={() => navigate(user?.role === 'supplier' ? '/supplier/billing' : '/pos')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground">
              <ShoppingCart className="w-3.5 h-3.5" /> Point of Sale
            </button>
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm">
              <History className="w-3.5 h-3.5" /> Sales History
            </button>
            {user?.role === 'supplier' && (
              <button onClick={() => navigate('/supplier/billing?view=invoices')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground">
                <Truck className="w-3.5 h-3.5" /> Sent Invoices
              </button>
            )}
          </div>
        </div>
      )}

      {/* Scrollable body */}
      <div className="flex-1 overflow-auto p-6 lg:p-8 space-y-5">

        {/* Page-wide period toggle — the single range control for the chart AND the list below */}
        <div className="flex flex-wrap items-center gap-2">
          {PERIODS.map((p, i) => (
            <button key={p.label} onClick={() => setActivePeriod(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                activePeriod === i
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
              }`}>
              {p.label}
            </button>
          ))}
          {activePeriod === CUSTOM_PERIOD_INDEX && (
            <div className="flex flex-wrap items-center gap-3 text-xs ml-1">
              <label className="flex items-center gap-1.5 text-muted-foreground">
                From
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                  className="border border-border rounded-md px-2 py-1 text-foreground" />
              </label>
              <label className="flex items-center gap-1.5 text-muted-foreground">
                To
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                  className="border border-border rounded-md px-2 py-1 text-foreground" />
              </label>
            </div>
          )}
        </div>

        {/* Sales Overview chart — same as Dashboard, scaled down; follows the period toggle above */}
        <SalesOverviewChart
          compact
          startDate={dateRange.start}
          endDate={dateRange.end}
          showPeriodControls={false}
          showTotals={false}
          onTotalsChange={setChartTotals}
          onRefresh={fetchSales}
          headerActions={
            <>
              <button onClick={() => navigate('/reports')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-border text-xs hover:bg-muted/60">
                <BarChart2 className="h-3 w-3" /> Advanced Reports
              </button>
              <button
                onClick={() => productViewMode ? exportProductCsv(productRows, searchQ) : exportCsv(mainBills)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90">
                <Download className="h-3 w-3" /> Export CSV
              </button>
            </>
          }
        />

        {/* Search + payment mode + totals — one row, no dead space between chart and table */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative max-w-2xl flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
            <input
              ref={searchRef}
              type="text"
              value={searchQ}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => { loadCatalog(); if (searchQ.trim()) setShowDropdown(true); }}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              placeholder="Search product, brand, barcode… or bill no / customer"
              className={`w-full pl-9 pr-4 py-2 rounded-lg border text-sm focus:outline-none focus:ring-1 focus:ring-primary ${
                productViewMode ? 'border-primary bg-primary/5' : 'border-border'
              }`}
            />

            {/* Live product dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto"
              >
                {searchResults.map((p, i) => (
                  <button
                    key={p.catalog_id}
                    onMouseDown={() => selectProduct(p)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-muted/60 text-left ${i === selectedIdx ? 'bg-primary/10' : ''}`}
                  >
                    <div className="min-w-0 mr-4">
                      <div className="font-medium truncate">{p.name || p.product_name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[p.brand, p.category, p.pack_size].filter(Boolean).join(' · ')}
                        {p.barcode && <span className="ml-2 font-mono">{p.barcode}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold text-emerald-700">{fmt(p.mrp ?? 0)}</div>
                      <div className="text-xs text-muted-foreground">Stock: {p.stock ?? 0}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Payment mode pills — conserve vertical space by sharing the search row */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 flex-nowrap shrink-0">
            {[
              { label: 'All',   value: '' },
              { label: 'Cash',  value: 'cash',  cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
              { label: 'Card',  value: 'card',  cls: 'bg-blue-100 text-blue-700 border-blue-300' },
              { label: 'UPI',   value: 'upi',   cls: 'bg-purple-100 text-purple-700 border-purple-300' },
              { label: 'Khata', value: 'khata', cls: 'bg-orange-100 text-orange-700 border-orange-300' },
            ].map(({ label, value, cls }) => {
              const active = paymentModeFilter === value;
              return (
                <button key={value} onClick={() => { setPaymentModeFilter(value); setPage(1); }}
                  className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                    active ? (cls || 'bg-primary text-primary-foreground border-primary')
                           : 'bg-background text-muted-foreground border-border hover:bg-muted/60'}`}>
                  {label}
                </button>
              );
            })}
          </div>

          {/* Camera scan — mobile only; desktop users type/paste barcodes */}
          {isMobile && (
            <button
              onClick={() => { loadCatalog(); setInlineCameraOpen(v => !v); }}
              disabled={barcodeLoading}
              title="Scan barcode with camera"
              className="p-3 rounded-lg border border-border hover:bg-muted/60 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary shrink-0"
            >
              {barcodeLoading
                ? <Loader2 className="h-6 w-6 animate-spin" />
                : <Camera className="h-6 w-6" />}
            </button>
          )}

          {productViewMode && (
            <button onClick={clearProductSearch}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium border border-primary/30 hover:bg-primary/20 shrink-0">
              <Package className="h-3 w-3" /> Product View ✕
            </button>
          )}

          {/* Totals — single source of truth, pushed to the far right of this row */}
          {productViewMode && productSummary ? (
            <div className="flex flex-wrap items-center gap-2 text-xs ml-auto">
              <span className="px-2.5 py-1 rounded-lg bg-primary/10 text-primary font-semibold">
                {productSummary.totalQty.toFixed(2).replace(/\.00$/, '')} <span className="font-normal text-muted-foreground">units</span>
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 font-semibold">
                {fmt(productSummary.totalRevenue)} <span className="font-normal text-muted-foreground">revenue</span>
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-muted text-foreground font-semibold">
                {fmt(productSummary.avgUnitPrice)} <span className="font-normal text-muted-foreground">avg price</span>
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-muted text-foreground font-semibold">
                {productSummary.billCount} <span className="font-normal text-muted-foreground">bills</span>
              </span>
            </div>
          ) : (
            <div className="flex gap-3 ml-auto">
              <div className="bg-emerald-50 rounded-lg px-3 py-1.5 text-right">
                <p className="text-xs text-muted-foreground">Total Revenue</p>
                <p className="text-sm font-bold text-emerald-600">₹{Math.round(Number(chartTotals.revenue ?? 0)).toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-muted rounded-lg px-3 py-1.5 text-right">
                <p className="text-xs text-muted-foreground">Transactions</p>
                <p className="text-sm font-bold text-foreground">{Number(chartTotals.transactions ?? 0).toLocaleString('en-IN')}</p>
              </div>
            </div>
          )}
        </div>

        {/* ── Inline camera strip (mobile only) ── */}
        {inlineCameraOpen && isMobile && (
          <CameraScanner
            inline
            onDetected={barcode => {
              setInlineCameraOpen(false);
              handleCameraDetected(barcode);
            }}
            onClose={() => setInlineCameraOpen(false)}
          />
        )}

        {/* ── PRODUCT VIEW ─────────────────────────────────────────────────── */}
        {productViewMode ? (
          <>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Package className="h-4 w-4 text-primary" />
              <span>
                Showing <span className="font-semibold text-foreground">{productRows.length}</span> sale{productRows.length !== 1 ? 's' : ''} of
                {' '}<span className="font-semibold text-foreground">"{searchQ}"</span>
                {' '}across <span className="font-semibold text-foreground">{productSummary?.billCount ?? 0}</span> bill{(productSummary?.billCount ?? 0) !== 1 ? 's' : ''}
                {productSummary && <> · GST collected: <span className="font-semibold text-foreground">{fmt(productSummary.totalGst)}</span></>}
              </span>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-x-auto">
              {loading ? (
                <div className="text-center py-16 text-muted-foreground text-sm">Loading…</div>
              ) : prodPageRows.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground text-sm">No product sales found in this period.</div>
              ) : (
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="border-b border-border bg-muted/30">
                    <tr>
                      {['DATE & TIME', 'BILL NO', 'QTY', 'UNIT PRICE', 'DISCOUNT', 'GST', 'ITEM TOTAL', 'RECEIPT'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {prodPageRows.map((row, idx) => {
                      const hasDiscount = Number(row.discount || 0) > 0;
                      return (
                        <tr key={`${row._bill_number}-${row.catalog_id}-${idx}`} className="hover:bg-muted/20">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div>{fmtDate(row._sale_date)}</div>
                            <div className="text-xs text-muted-foreground">{fmtTime(row._sale_date)}</div>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleReceiptView(row._bill_number)}
                              className="font-mono text-xs text-primary hover:underline" title="View receipt">
                              {row._bill_number}
                            </button>
                            <div className="mt-0.5"><ModeBadge mode={row._payment_mode} /></div>
                          </td>
                          <td className="px-4 py-3 font-semibold">
                            {Number(row.quantity ?? 0).toFixed(2).replace(/\.00$/, '')}
                          </td>
                          <td className="px-4 py-3">{fmt(row.unit_price)}</td>
                          <td className="px-4 py-3">
                            {hasDiscount
                              ? <span className="text-amber-700 text-xs font-medium">
                                  {row.discount_type === 'percent' ? `${row.discount}%` : fmt(row.discount)}
                                </span>
                              : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">
                            {fmt(row.gst_amount ?? 0)}
                            {row.gst_rate > 0 && <div className="text-muted-foreground/70">{row.gst_rate}%</div>}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-emerald-700">{fmt(row.item_total)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleReceiptView(row._bill_number)}
                              className="text-muted-foreground hover:text-primary" title={`View receipt ${row._bill_number}`}>
                              <ScrollText className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {prodTotalPages > 1 && (
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={prodCurrentPage === 1}
                  className="px-3 py-1.5 rounded border border-border text-sm hover:bg-muted/60 disabled:opacity-40">Previous</button>
                <span className="text-sm text-muted-foreground">Page {prodCurrentPage} of {prodTotalPages}</span>
                <button onClick={() => setPage(p => Math.min(prodTotalPages, p + 1))} disabled={prodCurrentPage === prodTotalPages}
                  className="px-3 py-1.5 rounded border border-border text-sm hover:bg-muted/60 disabled:opacity-40">Next</button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* ── BILL VIEW ────────────────────────────────────────────────── */}
            <div className="bg-card border border-border rounded-lg overflow-x-auto">
              {loading ? (
                <div className="text-center py-16 text-muted-foreground text-sm">Loading…</div>
              ) : pageRows.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground text-sm">No sales found for this period.</div>
              ) : (
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="border-b border-border bg-muted/30">
                    <tr>
                      {['DATE & TIME', 'BILL NO', 'CUSTOMER', 'ITEMS', 'MODE', 'GST', 'TOTAL',
                        ...(user?.role === 'supplier' ? ['STATUS'] : []),
                        'ACTIONS'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {pageRows.map(sale => {
                      const isDeleted = sale.status === 'deleted';
                      const isEdited  = Array.isArray(sale.amendments) && sale.amendments.length > 0;
                      const isPending = sale._offline && sale._queue_status !== 'synced';
                      const isFailed  = sale._offline && sale._queue_status === 'failed';
                      const items     = Array.isArray(sale.items) ? sale.items : [];
                      const itemLabel = items.slice(0, 2).map(i => `${i.product_name} ×${i.quantity}`).join(', ')
                        + (items.length > 2 ? ` +${items.length - 2} more` : '');
                      return (
                        <tr key={sale.id || sale.bill_number} className={`hover:bg-muted/20 ${isDeleted ? 'opacity-60' : ''}`}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className={isDeleted ? 'line-through text-muted-foreground' : ''}>{fmtDate(sale.created_at)}</div>
                            <div className="text-xs text-muted-foreground">{fmtTime(sale.created_at)}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`font-mono text-xs ${isDeleted ? 'line-through text-muted-foreground' : ''}`}>{sale.bill_number}</span>
                              {isEdited  && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Edited</span>}
                              {isDeleted && <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-medium">Deleted</span>}
                              {isFailed  && <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">Sync Failed</span>}
                              {isPending && !isFailed && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Pending Sync</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {sale.customer_name
                              ? <span className={isDeleted ? 'line-through text-muted-foreground' : ''}>{sale.customer_name}</span>
                              : <span className="italic text-muted-foreground">Walk-in</span>}
                          </td>
                          <td className="px-4 py-3 max-w-[180px]">
                            <span className={`text-xs ${isDeleted ? 'line-through text-muted-foreground' : ''}`}>{itemLabel || '—'}</span>
                          </td>
                          <td className="px-4 py-3"><ModeBadge mode={sale.payment_mode} /></td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">{fmt(sale.total_gst ?? 0)}</td>
                          <td className="px-4 py-3">
                            <span className={`font-semibold ${isDeleted ? 'line-through text-muted-foreground' : 'text-emerald-700'}`}>{fmt(sale.total_amount)}</span>
                          </td>
                          {user?.role === 'supplier' && (
                            <td className="px-4 py-3">
                              <DeliveryStatusPill status={sale.delivery_status} />
                            </td>
                          )}
                          <td className="px-4 py-3">
                            {isPending ? (
                              <span className={`text-xs italic ${isFailed ? 'text-red-500' : 'text-muted-foreground'}`}>{isFailed ? 'Sync failed' : 'Awaiting sync'}</span>
                            ) : isDeleted ? (
                              <button onClick={() => handleRestore(sale.bill_number)}
                                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800" title="Restore">
                                <RotateCcw className="h-3.5 w-3.5" /> Restore
                              </button>
                            ) : (
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleReceiptView(sale.bill_number, sale)} className="text-muted-foreground hover:text-primary" title="View receipt"><ScrollText className="h-3.5 w-3.5" /></button>
                                <button onClick={() => handleEdit(sale)} className="text-muted-foreground hover:text-primary" title="Edit (amend)"><Pencil className="h-3.5 w-3.5" /></button>
                                <button onClick={() => setDeleteTarget(sale.bill_number)} className="text-muted-foreground hover:text-red-500" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded border border-border text-sm hover:bg-muted/60 disabled:opacity-40">Previous</button>
                <span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded border border-border text-sm hover:bg-muted/60 disabled:opacity-40">Next</button>
              </div>
            )}

            {/* Revision History */}
            {(historyBills.length > 0 || mainBills.some(s => s.amendments?.length > 0)) && (
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <button onClick={() => setHistoryOpen(o => !o)}
                  className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/30 transition-colors text-left">
                  <div className="flex items-center gap-2">
                    <History className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold text-sm text-foreground">Revision History</span>
                    <span className="text-xs text-muted-foreground font-normal">— bills that have been edited</span>
                  </div>
                  {historyOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </button>
                {historyOpen && (
                  <div className="border-t border-border">
                    {mainBills.filter(s => s.amendments?.length > 0).map(sale =>
                      sale.amendments.map((amend, idx) => (
                        <div key={`${sale.bill_number}-amend-${idx}`} className="px-5 py-3 border-b border-border last:border-b-0 bg-amber-50/40">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-xs font-medium text-foreground">{sale.bill_number}</span>
                                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Before edit</span>
                                <span className="text-xs text-muted-foreground">{fmtDate(amend.amended_at)} {fmtTime(amend.amended_at)}</span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {(amend.items || []).slice(0, 3).map(i => `${i.product_name} ×${i.quantity}`).join(', ')}
                                {(amend.items || []).length > 3 && ` +${amend.items.length - 3} more`}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-xs text-muted-foreground">{amend.payment_mode}</div>
                              <div className="text-sm font-semibold text-muted-foreground">{fmt(amend.total_amount)}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                    {historyBills.length > 0 && (
                      <table className="w-full text-sm">
                        <thead className="bg-muted/20 border-b border-border">
                          <tr>
                            {['DATE', 'BILL NO', 'ITEMS', 'MODE', 'TOTAL'].map(h => (
                              <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {historyBills.map(sale => {
                            const items     = sale.items ?? [];
                            const itemLabel = items.slice(0, 2).map(i => `${i.product_name} ×${i.quantity}`).join(', ')
                              + (items.length > 2 ? ` +${items.length - 2} more` : '');
                            return (
                              <tr key={sale.id || sale.bill_number} className="opacity-70 hover:bg-muted/20">
                                <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground text-xs">{fmtDate(sale.created_at)}</td>
                                <td className="px-4 py-2.5">
                                  <span className="font-mono text-xs line-through text-muted-foreground">{sale.bill_number}</span>
                                  {sale.amended_by && <div className="text-xs text-muted-foreground mt-0.5">→ {sale.amended_by}</div>}
                                </td>
                                <td className="px-4 py-2.5 max-w-[220px] text-xs text-muted-foreground">{itemLabel || '—'}</td>
                                <td className="px-4 py-2.5"><ModeBadge mode={sale.payment_mode} /></td>
                                <td className="px-4 py-2.5 text-sm font-semibold text-muted-foreground line-through">{fmt(sale.total_amount)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Delete dialog */}
        {deleteTarget && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-2xl w-full max-w-sm p-6">
              <h3 className="font-bold text-lg mb-2">Delete Sale?</h3>
              <p className="text-sm text-muted-foreground mb-5">
                Bill <span className="font-mono font-medium">{deleteTarget}</span> will be soft-deleted and stock will be restored.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted/60">Cancel</button>
                <button onClick={handleDelete} disabled={deleteLoading} className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">
                  {deleteLoading ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {receiptSale && (
          <ReceiptModal sale={receiptSale} storeProfile={storeProfile} onClose={() => setReceiptSale(null)} />
        )}
      </div>
    </div>
  );
}
